/**
 * Standard contract wording — the sentences the Contract page's
 * "We hereby submit specification for:" list is auto-generated from.
 *
 * Admins edit these on the Contract page ("Edit standard wording"). They are
 * stored company-wide inside org_settings.proposal_text under the `contract`
 * key (see shared/proposalText.ts), so they ride the same admin-only settings
 * save as the proposal's editable labels — no extra table or column.
 *
 * Same rules as the proposal wording: a MISSING key means "use the built-in
 * sentence", blank is never stored, and typing a line back to its built-in
 * text clears the override. So an org that never edited anything generates
 * exactly the contract it always did.
 *
 * Fill-ins: {decking}, {railing}, {fastener}, {demo} are replaced with the
 * job's selections when the line is generated.
 *
 * Lines that come from Pricing Admin (each item's proposal description —
 * Demolition, Stairs, Add Items) are NOT here; they are edited in Pricing
 * Admin.
 */

export type ContractTextKey =
  | "opening"
  | "structureNew"
  | "structureResurface"
  | "structureSleeper"
  | "decking"
  | "fastenerClips"
  | "fastenerNails"
  | "fastenerScrails"
  | "fastenerScrews"
  | "fastenerOther"
  | "railing"
  | "railingColorInName"
  | "railingSelectFlatTop"
  | "skirtingVertical"
  | "skirtingHorizontal"
  | "lattice"
  | "latticePictureFrame"
  | "demoFallback";

export type ContractText = Partial<Record<ContractTextKey, string>>;

export const CONTRACT_TEXT_DEFAULTS: Record<ContractTextKey, string> = {
  opening:
    "New deck to be built as per the sketch plans and 3D renderings that will be emailed prior for approval",
  structureNew:
    'Deck Structure: 14"x36" poured concrete footings with KDAT 4x4 support posts and 2x8 floor joists installed 16" on center. All hardware (tecos, bolts, strapping) to be hot-dipped galvanized.',
  structureResurface:
    "Deck Structure: Existing deck framing to remain. Any compromised framing will be repaired or replaced as needed. All new decking/railing will be installed per code and manufacturer specifications.",
  structureSleeper:
    "Deck Structure: Remove existing structure. Sleeper system installed to establish proper pitch and drainage. Decking installed per manufacturer requirements.",
  decking: "New decking to be installed will be {decking}, color to be determined.",
  fastenerClips:
    "Decking to be secured with Tiger Claw black-coated stainless steel hidden clips.",
  fastenerNails: "Decking to be secured with hot-dipped galvanized nails.",
  fastenerScrails:
    "Decking to be secured with stainless steel scrails (gun-driven fasteners combining the holding power of a screw with the speed of a nail).",
  fastenerScrews: "Decking to be secured with color-matched stainless steel screws.",
  fastenerOther: "Fasteners: {fastener}.",
  railing: "New railing to be installed will be {railing}, color to be determined.",
  railingColorInName: "New railing to be installed will be {railing}.",
  railingSelectFlatTop:
    "New railing to be installed will be {railing} with black round aluminum spindles.",
  skirtingVertical:
    "Underside of deck to be skirted using matching deck boards installed vertically.",
  skirtingHorizontal:
    "Underside of deck to be skirted using matching deck boards installed horizontally.",
  lattice: "Underside of deck to be covered using small diamond vinyl lattice, color TBD.",
  latticePictureFrame:
    "Underside of deck to be covered using small diamond vinyl lattice, color TBD, with picture frame trim.",
  demoFallback:
    "Demolition: {demo} — Removal and disposal of existing materials as required.",
};

/** How the editor groups and names the lines. Order = the order on the contract. */
export const CONTRACT_TEXT_GROUPS: {
  title: string;
  items: { key: ContractTextKey; label: string }[];
}[] = [
  { title: "Opening line", items: [{ key: "opening", label: "Every contract" }] },
  {
    title: "Deck structure",
    items: [
      { key: "structureNew", label: "New Construction / Second Story" },
      { key: "structureResurface", label: "Resurface / Second Story Resurface" },
      { key: "structureSleeper", label: "Sleeper System / Second Story Sleeper" },
    ],
  },
  {
    title: "Demolition",
    items: [
      {
        key: "demoFallback",
        label: "Only when the demo item has no description in Pricing Admin",
      },
    ],
  },
  { title: "Decking", items: [{ key: "decking", label: "Every contract with decking" }] },
  {
    title: "Fasteners",
    items: [
      { key: "fastenerClips", label: "Hidden clips" },
      { key: "fastenerNails", label: "Nails" },
      { key: "fastenerScrails", label: "Scrails" },
      { key: "fastenerScrews", label: "Screws" },
      { key: "fastenerOther", label: "Any other fastener" },
    ],
  },
  {
    title: "Railing",
    items: [
      { key: "railing", label: "Most railings" },
      { key: "railingColorInName", label: "Railing name already says the color (e.g. wht, blk)" },
      { key: "railingSelectFlatTop", label: "Trex Select Flat Top" },
    ],
  },
  {
    title: "Skirting",
    items: [
      { key: "skirtingVertical", label: "Board skirting, vertical" },
      { key: "skirtingHorizontal", label: "Board skirting, horizontal" },
      { key: "lattice", label: "Lattice" },
      { key: "latticePictureFrame", label: "Lattice with picture frame (PF)" },
    ],
  },
];

/** "Used for" label per line, from the groups above. */
export const CONTRACT_TEXT_LABELS: Record<ContractTextKey, string> = (() => {
  const out = {} as Record<ContractTextKey, string>;
  CONTRACT_TEXT_GROUPS.forEach((g) =>
    g.items.forEach(({ key, label }) => {
      out[key] = g.items.length > 1 || g.title === "Demolition" ? `${g.title}: ${label}` : label;
    })
  );
  return out;
})();

/** Fill-ins each line should keep, so the editor can warn when one is lost. */
export const CONTRACT_TEXT_FILLINS: Partial<Record<ContractTextKey, string>> = {
  decking: "{decking}",
  fastenerOther: "{fastener}",
  railing: "{railing}",
  railingColorInName: "{railing}",
  railingSelectFlatTop: "{railing}",
  demoFallback: "{demo}",
};

export const CONTRACT_TEXT_MAX = 600;

const KEYS = Object.keys(CONTRACT_TEXT_DEFAULTS) as ContractTextKey[];

/** One line, trimmed, capped. Contract lines are one bullet each, so line
 *  breaks inside a line would split it into two bullets. */
export function cleanContractLine(raw: string): string {
  return String(raw || "")
    .replace(/[\r\n]+/g, " ")
    .slice(0, CONTRACT_TEXT_MAX);
}

export function normalizeContractText(raw: unknown): ContractText {
  const out: ContractText = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const r = raw as Record<string, unknown>;
  KEYS.forEach((k) => {
    const v = r[k];
    if (typeof v !== "string") return;
    const t = cleanContractLine(v).trim();
    if (t && t !== CONTRACT_TEXT_DEFAULTS[k]) out[k] = t;
  });
  return out;
}

/** The template for a line: the override, else the built-in sentence. */
export function contractTemplate(
  t: ContractText | null | undefined,
  key: ContractTextKey
): string {
  const v = t?.[key];
  return typeof v === "string" && v.trim() ? v.trim() : CONTRACT_TEXT_DEFAULTS[key];
}

/** The finished line, fill-ins replaced. */
export function contractLine(
  t: ContractText | null | undefined,
  key: ContractTextKey,
  vars: Partial<Record<"decking" | "railing" | "fastener" | "demo", string>> = {}
): string {
  return contractTemplate(t, key).replace(
    /\{(decking|railing|fastener|demo)\}/g,
    (_m, name: string) => (vars as any)[name] ?? ""
  );
}

/** Apply one edit: typed back to the default (or blank) = override removed. */
export function setContractLine(
  t: ContractText | null | undefined,
  key: ContractTextKey,
  raw: string
): ContractText {
  const next: ContractText = { ...(t || {}) };
  const v = cleanContractLine(raw).trim();
  if (v && v !== CONTRACT_TEXT_DEFAULTS[key]) next[key] = v;
  else delete next[key];
  return normalizeContractText(next);
}

/** Which Deck Structure line a construction type gets. The type is the
 *  construction_options item NAME from Pricing Admin, which is spelled
 *  inconsistently ("New Construction", "ReSurface", "Second_Story",
 *  "Sleeper System", "Second  Story Sleeper"), so match on words, not exact
 *  strings. Resurface and sleeper win over "second story" (a second-story
 *  resurface keeps its framing). */
export function structureKeyFor(constructionType: string): ContractTextKey | null {
  const c = String(constructionType || "")
    .toLowerCase()
    .replace(/[_\s]+/g, " ")
    .trim();
  if (!c) return null;
  if (c.includes("sleeper")) return "structureSleeper";
  if (c.includes("resurface") || c.includes("redeck")) return "structureResurface";
  if (c.includes("new construction") || c.includes("second story")) return "structureNew";
  return null;
}
